import { useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../client/supabaseClient";
import AuthRedirect from "../client/AuthRedirect";

import {
    Box,
    Button,
    Container,
    IconButton,
    InputAdornment,
    LinearProgress,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Paper,
    TextField,
    Typography,
} from "@mui/material";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LockIcon from "@mui/icons-material/Lock";
import EmailIcon from "@mui/icons-material/Email";

const SignUp = () => {
    const [status, setStatus] = useState("");
    const [formValues, setFormValues] = useState({
        email: "",
        password: "",
        confirmPassword: "",
    });
    const [errorMessage, setErrorMessage] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormValues({ ...formValues, [e.target.name]: e.target.value });
    };

    // Password rules
    const rules = [
        { label: "At least 6 characters", test: (pw: string) => pw.length >= 6 },
        { label: "At least one lowercase letter", test: (pw: string) => /[a-z]/.test(pw) },
        { label: "At least one uppercase letter", test: (pw: string) => /[A-Z]/.test(pw) },
        { label: "At least one number", test: (pw: string) => /[0-9]/.test(pw) },
        { label: "At least one special character", test: (pw: string) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?`~]/.test(pw) },
    ];

    const getPasswordStrength = (password: string): number => {
        let score = 0;
        rules.forEach((rule) => {
            if (rule.test(password)) score++;
        });
        return (score / rules.length) * 100;
    };

    const allRulesPassed = rules.every((rule) => rule.test(formValues.password));
    const passwordsMatch = formValues.password && formValues.password === formValues.confirmPassword;
    const isFormValid = allRulesPassed && passwordsMatch && formValues.email.length > 0;

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setErrorMessage("");

        if (!isFormValid) {
            setErrorMessage("Please fix the issues before creating an account.");
            return;
        }

        setStatus("Creating account...");
        const { error } = await supabase.auth.signUp({
            email: formValues.email,
            password: formValues.password,
        });

        if (error) {
            setErrorMessage(error.message);
        }
        setStatus("");
    };

    const strength = getPasswordStrength(formValues.password);

    return (
        <AuthRedirect>
            <Container maxWidth="sm">
                <Paper
                    elevation={4}
                    sx={{ p: 4, mt: 8, borderRadius: 3 }}
                    component="form"
                    onSubmit={handleSubmit}
                >
                    <Typography variant="h4" align="center" gutterBottom>
                        Sign Up
                    </Typography>
                    <Typography
                        variant="body2"
                        align="center"
                        sx={{ color: "text.secondary", mb: 3 }}
                    >
                        Demo app, please don’t use your real email or password
                    </Typography>

                    {/* Email field */}
                    <TextField
                        fullWidth
                        margin="normal"
                        label="Email"
                        name="email"
                        type="email"
                        onChange={handleInputChange}
                        required
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <EmailIcon color="action" />
                                </InputAdornment>
                            ),
                        }}
                    />

                    {/* Password field */}
                    <TextField
                        fullWidth
                        margin="normal"
                        label="Password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        onChange={handleInputChange}
                        required
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <LockIcon color="action" />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowPassword((prev) => !prev)}
                                        edge="end"
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    {/* Confirm password */}
                    <TextField
                        fullWidth
                        margin="normal"
                        label="Retype Password"
                        name="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        onChange={handleInputChange}
                        required
                        error={!!formValues.confirmPassword && !passwordsMatch}
                        helperText={
                            formValues.confirmPassword && !passwordsMatch
                                ? "Passwords do not match"
                                : ""
                        }
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <LockIcon color="action" />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                                        edge="end"
                                    >
                                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    {/* Strength meter */}
                    {formValues.password && (
                        <Box sx={{ mt: 2, mb: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                Password Strength:
                            </Typography>
                            <LinearProgress
                                variant="determinate"
                                value={strength}
                                sx={{
                                    height: 8,
                                    borderRadius: 5,
                                    backgroundColor: "#eee",
                                    "& .MuiLinearProgress-bar": {
                                        backgroundColor:
                                            strength < 40
                                                ? "#d32f2f"
                                                : strength < 80
                                                    ? "#f9a825"
                                                    : "#2e7d32",
                                    },
                                }}
                            />
                        </Box>
                    )}

                    {/* Checklist */}
                    {formValues.password && (
                        <Box sx={{ mt: 2, mb: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                Password requirements:
                            </Typography>
                            <List dense>
                                {rules.map((rule, idx) => {
                                    const passed = rule.test(formValues.password);
                                    return (
                                        <ListItem key={idx}>
                                            <ListItemIcon>
                                                {passed ? (
                                                    <CheckCircleIcon color="success" fontSize="small" />
                                                ) : (
                                                    <CancelIcon color="error" fontSize="small" />
                                                )}
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={rule.label}
                                                primaryTypographyProps={{
                                                    style: { color: passed ? "green" : "red" },
                                                }}
                                            />
                                        </ListItem>
                                    );
                                })}
                            </List>
                        </Box>
                    )}

                    {errorMessage && (
                        <Typography color="error" align="center" sx={{ mb: 2 }}>
                            {errorMessage}
                        </Typography>
                    )}
                    {status && (
                        <Typography color="primary" align="center" sx={{ mb: 2 }}>
                            {status}
                        </Typography>
                    )}

                    <Button
                        fullWidth
                        type="submit"
                        variant="contained"
                        size="large"
                        sx={{ mt: 1, mb: 2, borderRadius: 2 }}
                        disabled={!isFormValid}
                    >
                        Create Account
                    </Button>

                    <Typography variant="body2" align="center">
                        Already have an account?{" "}
                        <Link to="/sign-in" style={{ color: "#1976d2" }}>
                            Sign In
                        </Link>
                    </Typography>
                </Paper>
            </Container>
        </AuthRedirect>
    );
};

export default SignUp;
